import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen pt-28 pb-20">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[#a1a1aa] text-sm mb-8">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-white">Privacy Policy</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Privacy <span className="text-[#dc2626]">Policy</span>
          </h1>
          <p className="text-[#71717a] text-sm mb-12">Last updated: 01 January 2026</p>

          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Introduction</h2>
              <p className="text-[#a1a1aa] leading-relaxed">
                Welcome to Cube Multimedia ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website or use our services. We are committed to protecting your privacy and ensuring the security of your personal information. By accessing or using our website or services, you consent to the practices described in this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Information We Collect</h2>
              
              <h3 className="text-lg font-medium text-white mb-3">Personal Information</h3>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc list-inside text-[#a1a1aa] space-y-2 mb-4">
                <li>Register for an account</li>
                <li>Make a purchase</li>
                <li>Contact us through our website</li>
                <li>Subscribe to our newsletter</li>
                <li>Participate in surveys or provide feedback</li>
                <li>Use our services</li>
              </ul>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                This personal information may include but is not limited to:
              </p>
              <ul className="list-disc list-inside text-[#a1a1aa] space-y-2">
                <li>Name</li>
                <li>Email address</li>
                <li>Postal address</li>
                <li>Phone number</li>
                <li>Payment information</li>
              </ul>

              <h3 className="text-lg font-medium text-white mb-3 mt-6">Non-Personal Information</h3>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                We may also collect non-personal information, such as:
              </p>
              <ul className="list-disc list-inside text-[#a1a1aa] space-y-2">
                <li>IP addresses</li>
                <li>Browser type</li>
                <li>Internet service provider</li>
                <li>Referring and exit pages</li>
                <li>Operating system</li>
                <li>Date and time stamps</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Use of Information</h2>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                We may use your personal information for various purposes, including:
              </p>
              <ul className="list-disc list-inside text-[#a1a1aa] space-y-2">
                <li>To provide and maintain our services</li>
                <li>To process transactions</li>
                <li>To send updates, newsletters, and promotional materials</li>
                <li>To respond to inquiries and provide customer support</li>
                <li>To analyze and improve our website and services</li>
                <li>To detect, prevent, and address technical issues</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Disclosure of Information</h2>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                We may share your personal information with:
              </p>
              <ul className="list-disc list-inside text-[#a1a1aa] space-y-2 mb-4">
                <li>Service providers and vendors who assist us in operating our website and providing our services</li>
                <li>Legal authorities and law enforcement agencies when required by applicable law or in response to legal requests</li>
                <li>Business partners for marketing and promotional purposes (with your consent)</li>
              </ul>
              <p className="text-[#a1a1aa] leading-relaxed">
                We will not sell, trade, or rent your personal information to third parties for their marketing purposes without your explicit consent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-[#a1a1aa] leading-relaxed">
                Our website may use cookies and similar tracking technologies to collect information and enhance your browsing experience. You can choose to disable cookies through your browser settings, but this may affect certain features of our website.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Security</h2>
              <p className="text-[#a1a1aa] leading-relaxed">
                We employ reasonable security measures to protect your personal information. However, no method of transmission over the internet or electronic storage is entirely secure, and we cannot guarantee the absolute security of your data.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Your Choices</h2>
              <p className="text-[#a1a1aa] leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-[#a1a1aa] space-y-2">
                <li>Access, correct, or delete your personal information</li>
                <li>Opt out of receiving marketing communications</li>
                <li>Withdraw consent to our use of your personal information</li>
                <li>Request information about our data practices</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Changes to this Privacy Policy</h2>
              <p className="text-[#a1a1aa] leading-relaxed">
                We reserve the right to update and modify this Privacy Policy from time to time. Any changes will be posted on this page with a revised "Last Updated" date. Continued use of our website and services after such changes constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">Contact Us</h2>
              <p className="text-[#a1a1aa] leading-relaxed">
                If you have questions, concerns, or requests related to this Privacy Policy or our data practices, please contact us at{" "}
                <a href="mailto:contact@cubemultimedia.in" className="text-[#dc2626] hover:underline">
                  contact@cubemultimedia.in
                </a>
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
